import {
	BlcLoader,
	Body,
	Button,
	FlexBox,
	FormControl,
	FormError,
	Input,
	OpenLaneLogo,
	dsColors,
} from '@Backlot-Cars/archie';
import STATUS_CODES from 'constants/statusCodes';
import { publicEnvConfig } from 'helpers/env';
import OktaService from 'helpers/okta';
import { getQueryParams } from 'helpers/queryParams';
import { treatments } from 'helpers/split/treatments';
import { getSessionStorageItem, setSessionStorageItem } from 'helpers/sessionStorage';
import { getPathFromURL, getUtmAndAFCParamsString } from 'helpers/url';
import useTreatments from 'hooks/useTreatments';
import Cookies from 'js-cookie';
import { useTranslation } from 'next-i18next';
import Router, { useRouter } from 'next/router';
import AuthFormActions from 'partials/shared/user_form/_auth_form_actions';
import { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import UserSessionService from 'services/navigation/userSession.service';
import { setFormValues } from 'store/slices/signUp/signUpSlice';
import BlcAppStores from '../../components/BlcFooter/BlcAppStores';
import { getDisclaimerType, isUserForcedToConnectWithOkta } from './connect-okta/disclaimerTypes';
import OktaExistingSessionBanner from './okta-existing-session-banner';
import OktaSignInWidget from './okta-login-wigdet';
import { isBypassMode, loginModes, resolveLoginMode } from 'helpers/signIn/loginMode';
import { AFC_AUTH_FAILURE_RETRY_SEEN_KEY } from './constants';
import {
	AppStoreIcons,
	ForgotPasswordLink,
	FormWrapper,
	Grouper,
	LoginContainer,
	LogoContainer,
	StyledHeadline,
	StyledMessage,
} from './styles';

const SectionLanding = (props) => {
	const { t } = useTranslation(['common', 'signin', 'signin-afc']);
	const router = useRouter();
	const oktaOnlyLoginEnabled = useTreatments(treatments.buy_663_okta_only_login_react);
	const [showOktaExistingSessionBanner, setShowOktaExistingSessionBanner] = useState(false);
	const [error, setError] = useState();
	const [loading, setLoading] = useState(false);
	const {
		passwordForgot,
		signUpUrl,
		refererUrl,
		setForceOktaConnectionDisclaimerType,
		afcSignIn,
		setOktaConnectionEmail,
	} = props;
	const queryParamsToForward = getUtmAndAFCParamsString(getQueryParams(refererUrl));
	const errorMessage = t('common:error');
	const translation = afcSignIn ? 'signin-afc' : 'signin';
	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		setValue,
	} = useForm();
	const dispatch = useDispatch();

	const isDuplicateEmailRedirect = router.query.duplicate_email === 'true';
	const redirectedEmail = router.query.email ? decodeURIComponent(router.query.email) : '';

	const loginMode = resolveLoginMode({ modeParam: router.query.mode, oktaOnlyLoginEnabled });
	const shouldBypassOkta = isBypassMode(loginMode);
	const shouldShowOktaWidget = loginMode === loginModes.oktaonly;

	const checkOktaProfile = async ({ username, password }) => {
		let useOktaLogin;
		try {
			const response = await UserSessionService.instance.checkOktaProfile({ username, password });
			if (response?.status === STATUS_CODES.OK) useOktaLogin = true;
		} catch {
			useOktaLogin = false;
		}
		return useOktaLogin;
	};

	const redirectAfterLogin = (locationFromServer) => {
		if (refererUrl) {
			location.replace(getPathFromURL(refererUrl, '/home'));
		} else {
			Router.push(getPathFromURL(locationFromServer || '/home', '/home'));
		}
	};

	const loginWithRails = async (password, email) => {
		try {
			const result = await UserSessionService.instance.signIn(email, password, queryParamsToForward);
			redirectAfterLogin(result?.headers?.location);
			return true;
		} catch (error) {
			if (isUserForcedToConnectWithOkta(error)) {
				setForceOktaConnectionDisclaimerType(getDisclaimerType(error));
				setOktaConnectionEmail(email);
			} else {
				setError(error?.error?.message || errorMessage);
			}
		}
	};

	const redirectToAfcFastPath = (profile = {}) => {
		const signUpParams = {
			...getQueryParams(refererUrl),
			utm_medium: 'afc_okta',
		};
		const utm = JSON.parse(Cookies.get('utm') || '{}');
		utm.utm_medium = signUpParams.utm_medium;
		Cookies.set('utm', JSON.stringify(utm));
		if (profile.email || profile.firstName || profile.lastName) {
			dispatch(
				setFormValues({
					email: profile.email || '',
					firstName: profile.firstName || '',
					lastName: profile.lastName || '',
				}),
			);
		}
		Router.push({ pathname: '/sign_up', query: signUpParams });
	};

	const redirectToOrganicSignUpFromAfcAuthFailure = () => {
		setError('');
		setLoading(true);

		const hasSeenAfcAuthFailureRetry = getSessionStorageItem(AFC_AUTH_FAILURE_RETRY_SEEN_KEY) === 'true';

		if (hasSeenAfcAuthFailureRetry) {
			Router.push({ pathname: '/sign_up' });
			return;
		}

		setSessionStorageItem(AFC_AUTH_FAILURE_RETRY_SEEN_KEY, 'true');
		Router.push({ pathname: '/sign_up', query: { from_afc_auth_failure: true } });
	};

	const loginWithOkta = async (password, email) => {
		const { success, errorMessage, afcOktaFastPath } = await OktaService.getInstance().loginUsingOurForm(
			email,
			password,
			queryParamsToForward,
		);
		if (afcOktaFastPath) {
			redirectToAfcFastPath({ email });
			return false;
		}
		if (success) {
			redirectAfterLogin();
		} else {
			setError(errorMessage);
		}
		return success;
	};

	const signInWithRailsToken = async (accessToken) => {
		try {
			const response = await UserSessionService.instance.signIn('', '', queryParamsToForward, accessToken);
			if (response?.data?.success === false && response?.data?.data?.afc_okta_fast_path) {
				return { success: false, afcOktaFastPath: true };
			}
		} catch (error) {
			if (error?.error?.success === false && error?.error?.data?.afc_okta_fast_path) {
				return { success: false, afcOktaFastPath: true };
			}
			return {
				success: false,
				status: error?.status,
				errorMessage: error?.error?.message || 'GENERIC_ERROR_RESPONSE.errorMessage',
			};
		}

		return { success: true };
	};

	const loginWithAccessToken = async (accessToken) => {
		setError('');
		setLoading(true);
		const { success, status, errorMessage, afcOktaFastPath } = await signInWithRailsToken(accessToken);
		if (afcOktaFastPath) {
			let profile = {};
			try {
				const session = await OktaService.getInstance().checkSession();
				profile = session?.userProfile || {};
			} catch (error) {
				profile = {};
			}
			redirectToAfcFastPath(profile);
			return false;
		}
		if (success) {
			redirectAfterLogin();
		} else if (afcSignIn && status === STATUS_CODES.UNAUTHORIZED) {
			redirectToOrganicSignUpFromAfcAuthFailure();
		} else {
			setError(errorMessage);
			setLoading(false);
		}

		return success;
	};

	const login = async (email, password) => {
		setLoading(true);
		setError('');

		if (shouldBypassOkta) {
			const successfulLogin = await loginWithRails(password, email);
			if (!successfulLogin) setLoading(false);
			return;
		}

		const useOktaLogin = await checkOktaProfile({ username: email, password });
		const successfulLogin =
			useOktaLogin ? await loginWithOkta(password, email) : await loginWithRails(password, email);

		if (!successfulLogin) setLoading(false);
	};

	const signUp = async (email) => {
		const { success, firstName, lastName } = await OktaService.getInstance().checkIfAfcUser(email);
		const utm = JSON.parse(Cookies.get('utm') || '{}');
		let signUpParams;

		if (success) {
			signUpParams = getQueryParams(refererUrl);
			signUpParams.utm_medium = 'afc_okta';
			dispatch(setFormValues({ email, firstName, lastName }));
		} else {
			signUpParams = { utm_medium: 'afc' };
		}

		utm.utm_medium = signUpParams.utm_medium;
		Cookies.set('utm', JSON.stringify(utm));
		Router.push({ pathname: '/sign_up', query: signUpParams });
	};

	const onSubmit = () => {
		if (!loading) {
			const { email, password } = getValues();
			const doSubmit = afcSignIn ? signUp : login;
			doSubmit(email, password);
		}
	};

	const onKeyDown = (event) => {
		if (event.key === 'Enter') {
			onSubmit();
		}
	};

	useLayoutEffect(() => {
		if (isDuplicateEmailRedirect && redirectedEmail) {
			setValue('email', redirectedEmail);
		}
	}, [isDuplicateEmailRedirect, redirectedEmail, setValue]);

	return (
		<LoginContainer>
			<LogoContainer>
				<OpenLaneLogo height={32} width={200} />
			</LogoContainer>

			<FormWrapper>
				<FlexBox gap={16} flexDirection="column" flexGrow={1} justifyContent="flex-end">
					<StyledHeadline size="H1" bold>
						{t(`${translation}:landing.content.title`)}
					</StyledHeadline>
					<Body size="largest" color={dsColors.colorText.secondary}>
						{t(`${translation}:landing.content.subtitle`)}
					</Body>
				</FlexBox>

				<FlexBox margin={['lg', 0, 0, 0]} flexDirection="column" justifyContent="flex-start" flexGrow={4}>
					{shouldShowOktaWidget && !showOktaExistingSessionBanner && (
						<OktaSignInWidget
							loginWithAccessToken={loginWithAccessToken}
							errorMessage={error}
							requestIsLoading={loading}
							onInvalidCredentials={afcSignIn ? redirectToOrganicSignUpFromAfcAuthFailure : undefined}
						/>
					)}

					{loginMode !== loginModes.oktaless && (
						<OktaExistingSessionBanner
							setLoading={setLoading}
							queryParamsToForward={queryParamsToForward}
							setError={setError}
							redirectAfterLogin={redirectAfterLogin}
							redirectToAfcFastPath={redirectToAfcFastPath}
							show={showOktaExistingSessionBanner}
							setShowOktaExistingSessionBanner={setShowOktaExistingSessionBanner}
						/>
					)}

					{!showOktaExistingSessionBanner && !shouldShowOktaWidget && (
						<>
							{isDuplicateEmailRedirect && (
								<StyledMessage
									color={dsColors.colorSurface.secondary}
									borderColor={dsColors.colorSurface.tertiary}
									iconColor="primary"
								>
									<Body color={dsColors.colorText.info}>{t('signin:duplicateEmailRedirect')}</Body>
								</StyledMessage>
							)}

							<Grouper gap={24} margin={['xl', 0]}>
								<FormControl
									label={t(`${translation}:landing.content.fields.emailUsername`)}
									formErrors={errors}
									required
								>
									<Input
										maxLength="255"
										id="email"
										{...register('email', {
											required: t('common:emailUsernameRequired'),
										})}
										onKeyDown={onKeyDown}
									/>
								</FormControl>
								<FormControl
									label={t(`${translation}:landing.content.fields.password`)}
									formErrors={errors}
									required
								>
									<Input
										type="password"
										autoComplete="off"
										maxLength="255"
										id="password"
										{...register('password', {
											required: t('common:passwordRequired'),
										})}
										onKeyDown={onKeyDown}
									/>
								</FormControl>
							</Grouper>

							{Boolean(loading) && (
								<FlexBox justifyContent="center" padding="md">
									<BlcLoader size="larger" />
								</FlexBox>
							)}
							{error && (
								<FlexBox margin={[0, 0, 'xs', 0]}>
									<FormError>{error}</FormError>
								</FlexBox>
							)}

							<AuthFormActions
								submitText={t(`${translation}:landing.content.actions.logIn`)}
								onClickSubmit={handleSubmit(onSubmit)}
								dataTestId="sign-in-button"
							/>

							{!afcSignIn && (
								<ForgotPasswordLink>
									<Body>
										{t(`${translation}:landing.content.actions.forgot`)}{' '}
										<Button variant="link" href={passwordForgot} labelColor="brand">
											{t(`${translation}:landing.content.actions.requestReset`)}
										</Button>
									</Body>
								</ForgotPasswordLink>
							)}

							{!afcSignIn && (
								<FlexBox justifyContent="center" margin={['md', 0, 0, 0]}>
									<Body>
										{t(`${translation}:landing.content.actions.accountNeeded`)}{' '}
										<Button variant="link" href={signUpUrl} labelColor="brand">
											{t(`${translation}:landing.content.actions.signUp`)}
										</Button>
									</Body>
								</FlexBox>
							)}
						</>
					)}
				</FlexBox>

				<AppStoreIcons>
					<BlcAppStores flexBoxProps={{ flexDirection: 'row', gap: 'lg', justifyContent: 'center' }} />
				</AppStoreIcons>
				{publicEnvConfig.AUDIOEYE_URL && (
					<FlexBox alignSelf="center" margin={['md', 0, 0]}>
						<Button variant="link" labelColor="brand" onClick={() => Router.push('/accessibility')}>
							{t('signin:accessibility.manageSettings')}
						</Button>
					</FlexBox>
				)}
			</FormWrapper>
		</LoginContainer>
	);
};

export default SectionLanding;
